import { PolicyModalityCode } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { IIndexable } from "@common-shared/types/general.types"
import type {
    IPayer,
    IPractice,
    ISimplifiedPractice,
} from "@common-shared/types/responseData/commonResponseData.types"
import {
    IOneVerifyBillerGroup,
    IOneVerifyOrganizationUser,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { LookupHelper as NirvanaLookupHelper } from "@front-shared/helpers/lookup.helper"
import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types"

import { COVERAGE_CONFIG } from "../config/coverage.config"

export class LookupHelper extends NirvanaLookupHelper {
    /**
     * Convert object to array of rendered select options
     */
    static getRenderedOptionsFromObject<Key extends string | number>(
        values: Partial<Record<Key, string>>,
        ignoredKeys: Key[] = [],
    ): ISelectRenderedOption[] {
        return Object.keys(values)
            .filter(key => !ignoredKeys.includes(key as Key))
            .map(key => ({
                key,
                displayValue: values[key as Key] || key,
                value: key,
                dropdownDisplayValue: values[key as Key] || key,
            }))
    }

    /**
     * Get rendered simplified or full Practice value for dropdown
     */
    static getRenderedPractice(
        item: IPractice | ISimplifiedPractice,
    ): ISelectRenderedOption {
        const displayValue = item.name

        return {
            displayValue,
            dropdownDisplayValue: <PrimaryText>{displayValue}</PrimaryText>,
            dropdownDisplayValueAdditional: (
                <div>
                    <PrimaryText typography="caption">
                        ID: {item.id}
                    </PrimaryText>
                    <PrimaryText typography="caption">
                        Group NPI: {item.groupNPI}
                    </PrimaryText>
                </div>
            ),
            value: item,
        }
    }

    /**
     * Get rendered organization user value for dropdown
     */
    static getRenderedOrganizationUser(
        item: IOneVerifyOrganizationUser,
    ): ISelectRenderedOption {
        const displayValue = item.name || item.email

        return {
            displayValue,
            dropdownDisplayValue: <PrimaryText>{displayValue}</PrimaryText>,
            dropdownDisplayValueAdditional: (
                <div>
                    <div className="flex items-center justify-between">
                        <PrimaryText typography="caption">
                            ID: {item.id}
                        </PrimaryText>
                        <PrimaryText typography="caption">
                            {item.email}
                        </PrimaryText>
                    </div>
                    <PrimaryText typography="caption">
                        Role: {item.role?.label}
                    </PrimaryText>
                </div>
            ),
            value: item,
        }
    }

    /**
     * Get rendered biller group value for dropdown
     */
    static getRenderedBillerGroup(
        item: IOneVerifyBillerGroup,
    ): ISelectRenderedOption {
        const displayValue = item.name || item.id.toString()

        return {
            displayValue,
            dropdownDisplayValue: <PrimaryText>{displayValue}</PrimaryText>,
            dropdownDisplayValueAdditional: (
                <div>
                    <PrimaryText typography="caption">
                        ID: {item.id}
                    </PrimaryText>
                    <PrimaryText typography="caption">
                        Users: {item.users?.length || 0}
                    </PrimaryText>
                </div>
            ),
            value: item,
        }
    }

    /**
     * Get rendered modality codes for dropdown
     */
    static getRenderedModalityCodes(
        modalityCodes?: PolicyModalityCode[],
    ): ISelectRenderedOption[] {
        return (
            modalityCodes?.map(value => {
                return {
                    value,
                    displayValue:
                        COVERAGE_CONFIG.modalityCodeDisplayNameMapping[value],
                }
            }) || []
        )
    }

    /**
     * Get medicaid default payer
     */
    static getMedicaidDefaultPayer(): IPayer {
        return {
            id: -1,
            insuranceName: "Medicaid",
            payerId: "MEDICAID",
            isBCBS: false,
            isCoverageSupported: true,
            isDiscoverSupported: false,
            isAgenticPhoneSupported: false,
            isAgenticPortalSupported: false,
            isAssignmentOfBenefitsSupported: false,
            isNirvanaSupported: true,
        }
    }

    /**
     * Convert object (enum mapped) to array of rendered select options
     */
    static getRenderedOptionsFromObjectMapped<Key extends string>(
        values: IIndexable,
        ignoredKeys: Key[] = [],
    ): ISelectRenderedOption[] {
        return Object.keys(values)
            .filter(key => !ignoredKeys.includes(key as Key))
            .map(key => ({
                key,
                displayValue: key,
                value: key,
                dropdownDisplayValue: values[key],
            }))
    }

    /**
     * Convert object (enum mapped) to array of raw select options
     */
    static getRawOptionsFromObjectMapped<Key extends string>(
        values: IIndexable,
        ignoredKeys: Key[] = [],
    ): ISelectRawOption[] {
        return Object.keys(values)
            .filter(key => !ignoredKeys.includes(key as Key))
            .map(key => ({
                key,
                label: values[key],
                value: key,
            }))
    }
}
